import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Mail, Phone, MessageCircle, Send, Bot, User, 
  Loader2, Clock, MapPin, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ContactUs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! 👋 I'm your AI support assistant. How can I help you today? You can ask me about buying, selling, refunds, or any other questions about theailedger.ai!"
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to top on page load
  useEffect(() => {
    // scroll handled by <ScrollRestoration />
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!inputMessage.trim() || isTyping) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);

    try {
      const response = await axios.post(`${API_URL}/api/support/chat`, {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.message }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I'm having trouble connecting right now. Please try again or email us at support@theailedger.ai" 
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const quickQuestions = [
    "How do I get a refund?",
    "How do I become a seller?",
    "What are the platform fees?",
    "How does the affiliate program work?"
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketplace
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
            <p className="text-neutral-400">We're here to help! Choose your preferred way to reach us.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Methods */}
            <div className="space-y-6">
              {/* Email */}
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#00F0FF]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Email Support</h3>
                    <p className="text-neutral-400 text-sm mb-3">Best for detailed inquiries and documentation</p>
                    <a 
                      href="mailto:support@theailedger.ai"
                      className="text-[#00F0FF] hover:underline font-medium"
                    >
                      support@theailedger.ai
                    </a>
                    <p className="text-neutral-500 text-xs mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Response within 24-48 hours
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone - AI Support */}
              <div className="bg-gradient-to-br from-white/10 to-[#00F0FF]/10 border border-white/30 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white text-black/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-lg">AI Phone Support</h3>
                      <span className="text-xs bg-[#00FF94]/20 text-[#00FF94] px-2 py-0.5 rounded-full font-medium">
                        24/7
                      </span>
                    </div>
                    <p className="text-neutral-400 text-sm mb-3">
                      Speak with Alex, our AI assistant, anytime day or night
                    </p>
                    <a 
                      href="tel:+12532159534"
                      className="text-white hover:underline font-medium text-lg"
                    >
                      +1 (253) 215-9534
                    </a>
                    <p className="text-neutral-400 text-xs mt-2 flex items-center gap-1">
                      <Bot className="w-3 h-3 text-[#00F0FF]" />
                      AI-powered • Instant answers • No wait time
                    </p>
                    <div className="mt-3 text-xs text-neutral-500">
                      Ask about orders, products, refunds, and more!
                    </div>
                  </div>
                </div>
              </div>

              {/* Support Availability */}
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#00F0FF]" />
                  </div>
                  <div className="w-full">
                    <h3 className="font-bold text-lg mb-3">Support Availability</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-2 bg-[#00FF94]/5 rounded-lg border border-[#00FF94]/20">
                        <div className="flex items-center gap-2">
                          <Bot className="w-4 h-4 text-[#00FF94]" />
                          <span className="text-sm">AI Phone & Chat Support</span>
                        </div>
                        <span className="text-[#00FF94] font-bold text-sm">24/7</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-neutral-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-neutral-400" />
                          <span className="text-sm text-neutral-300">Email Response</span>
                        </div>
                        <span className="text-neutral-400 text-sm">24-48 hrs</span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-neutral-800/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-neutral-400" />
                          <span className="text-sm text-neutral-300">Human Escalation</span>
                        </div>
                        <span className="text-neutral-400 text-sm">Mon-Fri 9-6 EST</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Helpful Links */}
              <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                <h3 className="font-bold text-lg mb-4">Helpful Resources</h3>
                <div className="space-y-3">
                  <button 
                    onClick={() => navigate('/faqs')}
                    className="w-full flex items-center justify-between p-3 rounded-xl bg-neutral-800/50 hover:bg-neutral-800 transition-colors"
                  >
                    <span>Frequently Asked Questions</span>
                    <ExternalLink className="w-4 h-4 text-neutral-400" />
                  </button>
                  <button 
                    onClick={() => navigate('/terms')}
                    className="w-full flex items-center justify-between p-3 rounded-xl bg-neutral-800/50 hover:bg-neutral-800 transition-colors"
                  >
                    <span>Terms & Conditions</span>
                    <ExternalLink className="w-4 h-4 text-neutral-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* AI Chat */}
            <div className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden flex flex-col h-[600px]">
              {/* Chat Header */}
              <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#00F0FF]/10 to-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#00F0FF] flex items-center justify-center">
                    <Bot className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <h3 className="font-bold">AI Support Assistant</h3>
                    <p className="text-xs text-green-400 flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-400"></span>
                      Online - Instant replies
                    </p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {chatMessages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                      <div className="flex items-end gap-2">
                        {msg.role === 'assistant' && (
                          <div className="w-6 h-6 rounded-full bg-[#00F0FF] flex items-center justify-center flex-shrink-0">
                            <Bot className="w-3 h-3 text-black" />
                          </div>
                        )}
                        <div 
                          className={`rounded-2xl px-4 py-2 ${
                            msg.role === 'user' 
                              ? 'bg-[#00F0FF] text-black rounded-br-md' 
                              : 'bg-neutral-800 text-white rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-6 h-6 rounded-full bg-neutral-700 flex items-center justify-center flex-shrink-0">
                            <User className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="flex items-end gap-2">
                      <div className="w-6 h-6 rounded-full bg-[#00F0FF] flex items-center justify-center flex-shrink-0">
                        <Bot className="w-3 h-3 text-black" />
                      </div>
                      <div className="bg-neutral-800 rounded-2xl rounded-bl-md px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-neutral-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                          <span className="w-2 h-2 bg-neutral-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                          <span className="w-2 h-2 bg-neutral-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Questions */}
              {chatMessages.length <= 1 && (
                <div className="px-4 pb-2">
                  <p className="text-xs text-neutral-500 mb-2">Quick questions:</p>
                  <div className="flex flex-wrap gap-2">
                    {quickQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setInputMessage(q);
                          setTimeout(() => handleSendMessage(), 100);
                        }}
                        className="text-xs px-3 py-1.5 rounded-full bg-neutral-800 hover:bg-neutral-700 text-neutral-300 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="bg-neutral-800 border-white/10"
                    disabled={isTyping}
                  />
                  <Button 
                    type="submit"
                    disabled={!inputMessage.trim() || isTyping}
                    className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                  >
                    {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;
